import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DownloadStrip,
  Empty,
  InstallTheatre,
  Link,
  soundedPointerHandlers,
  ui,
  VoltFlash,
} from '../ui';
import { formatBytes } from '@goodbuild/rules/format';
import { titleArtPath } from '@goodbuild/rules/title-art';
import { playSound } from '../ui/sound';
import type { InstallTarget, LauncherEntitlement, Manifest } from '@api/views';
import { apiOrigin, useApi, useApiMutation } from '../api';
import { isLauncher } from '../is-launcher';
import { ensureRegistered } from '../launcher-device';
import {
  autoUpdate,
  bandwidthCap,
  chunksAtOnce,
  libraryRoot,
  updateCheckMinutes,
} from '../launcher-prefs';
import {
  canStart,
  cancelDownload,
  installedTitles,
  launch,
  onDownloadFailed,
  onDownloadProgress,
  onDownloadTokenExpired,
  onGameExited,
  onGameStarted,
  onInstallProgress,
  onInstalled,
  pauseDownload,
  pendingSessions,
  resumeDownload,
  runningGames,
  sessionReported,
  startDownload,
  stopGame,
  verify,
  type ContentServer,
  type DownloadProgress,
  type DownloadState,
  type InstallProgress,
  type InstallRecord,
  type PlaySession,
  type StartedPlan,
} from '../launcher-ipc';
import { Notice, Screen, Table } from './shared';

/**
 * Getting what you own onto this machine.
 *
 * The launcher half of `/library`, and deliberately a second screen rather than
 * a column added to the first. `/library` answers *what do I own*, which is a
 * question with the same answer on every device a person signs in on; this
 * answers *what is on this computer*, which is a different question that only
 * exists where there is a computer. Merging them would put a Download button
 * into a browser, where it can only ever be a lie.
 *
 * So this route is registered only when `isLauncher()` is true, and it refuses
 * on its own as well — see the guard below for why both.
 *
 * ## What actually happens when you press Download
 *
 * Four requests and then exactly one IPC call, in this order, and the order is
 * the server contract in `apps/launcher/README.md`:
 *
 *   1. `GET /v1/launcher/library` — what you own and which build each resolves
 *      to. One query for the whole screen.
 *   2. `GET /v1/launcher/manifest/:buildId?productId=` — the file list. Guarded
 *      by ownership of the *title* rather than by the build id being
 *      unguessable, because a manifest is a complete listing of a game and is
 *      worth more to somebody who has not bought it than the bytes are.
 *   3. `GET /v1/launcher/servers` — the ranked list to fetch from. A hint:
 *      `servers.rs` measures each and lets observed throughput override it.
 *   4. `POST /v1/launcher/entitlement/:productId` — a six-hour token proving
 *      you may fetch this title's chunks. It goes to the content server in
 *      `x-goodbuild-entitlement` and **never in the URL** (hard rule 25), which
 *      is enforced in `chunk_url` and is not something this file can get wrong.
 *
 * Then `start_download` plans it and runs it. **That used to be the first of a
 * hundred and sixty thousand calls** — this file fed `fetch_chunk` one hash at
 * a time, which for a 40GB build meant that many round trips through the
 * webview bridge, each returning a status object, before considering the bytes.
 * Worse, the command reported the state of the whole *job* rather than the
 * outcome of the *chunk*, so with four lanes in flight this file could not tell
 * which fetch had failed and re-swept the entire manifest to find out.
 *
 * The loop is `downloader.rs` now. What is left here subscribes: progress
 * arrives about four times a second, an expired entitlement token is re-minted
 * without asking anybody, and the install reports its own end.
 *
 * ## What this still does not do, said out loud
 *
 * **A session's `active_minutes` is always zero.** Section 5.4 defines activity
 * as input events and progression telemetry, and both come from inside the
 * game — a launcher watching from outside sees a process, not a controller. So
 * the launcher reports the elapsed minutes it genuinely measured and claims no
 * activity it did not observe, because every figure that reads `active_minutes`
 * is one that inflating would corrupt: refund eligibility, the thirty-minute
 * review gate, First Hundred fulfilment, a studio's public playtime.
 *
 * `sessionTick` stays on the IPC surface taking input and progression counts.
 * That is the seam a game-side SDK reports through, and until one exists this
 * is the honest number.
 */

/* -------------------------------------------------------------------------- */
/* What the server says                                                       */
/* -------------------------------------------------------------------------- */

interface LauncherLibrary {
  entitlements: LauncherEntitlement[];
  /** Keyed by product id. Null for a title on sale with no build pushed yet. */
  builds: Record<string, InstallTarget | null>;
}

/** Bars in the progress strip. See `Bar` for why it is a fixed number. */
const STRIP_BARS = 24;

/* -------------------------------------------------------------------------- */
/* Telling the platform what happened                                         */
/* -------------------------------------------------------------------------- */

/**
 * Install reporting: `POST /v1/launcher/installs`, and nothing it can break.
 *
 * Board task #70. An install is the one analytics family with no transactional
 * counterpart anywhere — there is no install table in D1, there never was one,
 * and `apps/api/src/lib/builds/installs` argues at length that there should
 * not be. What the platform gets instead is this: an event per attempt, saying
 * whether a download started, finished, failed or was abandoned, on which
 * platform, and how long it took. That is what answers "does 1.4.2 fail more
 * than 1.4.1", which is a question a studio asks and nothing here could answer.
 *
 * **A download must never fail because the platform could not be told about
 * it.** The server side of this guarantees its half — `emit` swallows its own
 * failures so analytics can never fail a request — but the *client* half is an
 * ordinary `fetch` that rejects on an offline machine, a 500, or a session that
 * expired mid-download. So every call goes through this function, which
 * swallows, and every call site fires it without awaiting. A player whose
 * network dropped is exactly the player whose install is most interesting and
 * least reportable, and losing the report is the correct trade.
 */
type Call = ReturnType<typeof useApiMutation>;

interface InstallReport {
  product_id: string;
  build_id: string;
  kind: 'install' | 'update';
  outcome: 'started' | 'completed' | 'failed' | 'cancelled';
  platform: 'windows' | 'macos' | 'linux';
  /**
   * Bytes actually moved after chunk deduplication, and **a real number now**.
   *
   * This used to be zero. It was read from `bytes_to_fetch`, which is summed
   * from a chunk-size index the platform published nowhere — so the field a
   * studio's build-health figures are computed from was empty by construction,
   * and every query had to filter `bytes_transferred > 0` to avoid it.
   *
   * What goes in instead is what the Rust side counted as the bytes arrived,
   * which needs no size index at all and is what the field always meant. It is
   * the *compressed* figure, because that is what crossed the wire and what a
   * player on a metered connection was billed for.
   */
  bytes_transferred: number;
  duration_seconds: number | null;
  failure_kind: string | null;
}

async function reportInstall(call: Call, report: InstallReport): Promise<void> {
  try {
    await call('/launcher/installs', { method: 'POST', body: JSON.stringify(report) });
  } catch {
    /*
     * Deliberately empty, and deliberately not logged.
     *
     * The two ordinary ways to get here are an offline machine and an expired
     * session, neither of which is a bug and neither of which the player can
     * act on. A console line per failed report would be noise in the one place
     * a launcher's real errors have to be findable.
     */
  }
}

/**
 * Which of three operating systems this is, and nothing finer.
 *
 * The only user-agent read in this application, and it reads it for a bucket
 * rather than for identity: the string never leaves the machine, only the word
 * `windows`, `macos` or `linux` does. `docs/analytics.md` §6 refuses to send a
 * full user agent anywhere for exactly this reason — it is one of the strongest
 * passive fingerprinting signals a request carries — and three buckets is the
 * shape that answers "which platform does this build fail on" without carrying
 * one.
 *
 * Falls through to `linux` rather than to an `unknown` bucket. This code only
 * runs inside the launcher's webview, which is one of the three, and an
 * `unknown` category would collect webview quirks rather than a real platform.
 */
function currentPlatform(): 'windows' | 'macos' | 'linux' {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/windows|win32|win64/i.test(agent)) return 'windows';
  if (/mac os x|macintosh/i.test(agent)) return 'macos';
  return 'linux';
}

/* -------------------------------------------------------------------------- */
/* The driver                                                                 */
/* -------------------------------------------------------------------------- */

type Phase =
  | 'idle'
  | 'preparing'
  | 'running'
  | 'paused'
  | 'stalled'
  | 'applying'
  | 'installed'
  | 'failed';

interface Progress {
  phase: Phase;
  /** What `start_download` said the work would be. Null until it answers. */
  plan: StartedPlan | null;
  /** The last `download-progress` event. */
  live: DownloadProgress | null;
  /** The last `install-progress` event, while files are being written. */
  apply: InstallProgress | null;
  error: string | null;
}

const IDLE: Progress = { phase: 'idle', plan: null, live: null, apply: null, error: null };

/**
 * What a download's own state means for the screen.
 *
 * `complete` becomes `applying` rather than a finished state, because a
 * download that has fetched every chunk has not installed anything yet — the
 * Rust side goes straight on to writing files, and the screen saying "done"
 * while a 40GB build is being assembled would be a lie a player acts on by
 * closing the launcher.
 */
function phaseOf(state: DownloadState, current: Phase): Phase {
  switch (state) {
    case 'queued':
    case 'running':
      return 'running';
    case 'paused':
      return 'paused';
    case 'stalled':
      return 'stalled';
    case 'complete':
      return current === 'installed' ? 'installed' : 'applying';
  }
}

/**
 * One download, held in a ref rather than in state.
 *
 * Much smaller than it used to be. This side no longer holds a queue, a cursor,
 * a retry list, a sweep counter or a guess at what the last chunk did — all of
 * that was bookkeeping for driving the fetch loop from JavaScript, and the loop
 * is in `downloader.rs` now. What is left is the handful of facts the *install
 * report* needs, which is the one thing the Rust side does not know about.
 */
interface Job {
  productId: string;
  buildId: string;
  kind: 'install' | 'update';
  /** `Date.now()` when the download was registered, for `duration_seconds`. */
  startedAtMs: number;
  /** Set once a terminal report has been sent, so a job reports its end once. */
  reported: boolean;
  /** Last known bytes off the wire, for the terminal report. */
  bytes: number;
}

function useDownloads(): {
  progressOf: (productId: string) => Progress;
  installedOf: (productId: string) => InstallRecord | null;
  checkOf: (productId: string) => string | null;
  /** Re-reads what is on disk. Stable, so effects may depend on it. */
  refreshInstalled: () => void;
  start: (productId: string, target: InstallTarget, productName: string) => void;
  pause: (productId: string) => void;
  resume: (productId: string) => void;
  cancel: (productId: string) => void;
  repair: (productId: string) => void;
} {
  const request = useApiMutation();
  const [progress, setProgress] = useState<Record<string, Progress>>({});
  const [installed, setInstalled] = useState<Record<string, InstallRecord>>({});
  const [checks, setChecks] = useState<Record<string, string>>({});
  const jobs = useRef(new Map<string, Job>());

  /**
   * Updates one title's progress from whatever it currently is.
   *
   * A function rather than a value, because every caller is an event handler
   * closed over at subscription time: reading `progress` directly would read
   * whatever it was when the effect ran, and a download would appear to freeze
   * at its first event. This is the same reason the subscriptions below depend
   * on nothing that changes per render.
   */
  const patch = useCallback((productId: string, change: (current: Progress) => Progress) => {
    setProgress((current) => ({
      ...current,
      [productId]: change(current[productId] ?? IDLE),
    }));
  }, []);

  const progressOf = useCallback((productId: string) => progress[productId] ?? IDLE, [progress]);

  const installedOf = useCallback((productId: string) => installed[productId] ?? null, [installed]);

  const checkOf = useCallback((productId: string) => checks[productId] ?? null, [checks]);

  /** What is on this machine, which is now a question with an answer. */
  const refreshInstalled = useCallback((): void => {
    if (!isLauncher()) return;

    void installedTitles()
      .then((records) => {
        setInstalled(Object.fromEntries(records.map((record) => [record.product_id, record])));
      })
      .catch(() => {
        // A launcher that cannot read its own install records still downloads
        // perfectly well; it just shows "Download" where it might have shown
        // "Update". Not worth a message on a screen about games.
      });
  }, []);

  useEffect(refreshInstalled, [refreshInstalled]);

  /**
   * Reports how a download ended, exactly once.
   *
   * `job.reported` rather than a per-outcome check, because a job that fails
   * and is then abandoned would otherwise report twice — and two rows for one
   * attempt inflate the failure rate of whichever build a player retried most,
   * which is precisely backwards.
   *
   * **`bytes_transferred` is a real number now.** It used to be zero, because
   * it was read from a plan built with no chunk-size index. What goes in is
   * what the Rust side counted as it arrived, which needs no index and is what
   * the field always meant.
   *
   * Not awaited by any caller. See `reportInstall`.
   */
  const reportEnd = useCallback(
    (job: Job, outcome: 'completed' | 'failed' | 'cancelled', failureKind: string | null): void => {
      if (job.reported) return;
      job.reported = true;

      void reportInstall(request, {
        product_id: job.productId,
        build_id: job.buildId,
        kind: job.kind,
        outcome,
        platform: currentPlatform(),
        bytes_transferred: job.bytes,
        duration_seconds: Math.round((Date.now() - job.startedAtMs) / 1000),
        failure_kind: failureKind,
      });
    },
    [request],
  );

  const mint = useCallback(
    async (productId: string): Promise<string> => {
      const minted = await request<{ token: string; expires_at: number }>(
        `/launcher/entitlement/${encodeURIComponent(productId)}`,
        { method: 'POST' },
      );
      return minted.token;
    },
    [request],
  );

  /*
   * The subscriptions, established once for the screen rather than per title.
   *
   * Every event carries its own `product_id`, so one handler serves every row —
   * and a per-row subscription would mean unsubscribing and resubscribing as
   * the library list re-renders, which is how events get missed.
   */
  useEffect(() => {
    if (!isLauncher()) return;

    const stops = [
      onDownloadProgress((live) => {
        const job = jobs.current.get(live.product_id);
        if (job) job.bytes = live.bytes_downloaded;

        patch(live.product_id, (current) => ({
          ...current,
          phase: phaseOf(live.state, current.phase),
          live,
          error: null,
        }));
      }),

      onInstallProgress((apply) => {
        patch(apply.product_id, (current) => ({ ...current, phase: 'applying', apply }));
      }),

      onInstalled((event) => {
        const job = jobs.current.get(event.product_id);
        if (job) reportEnd(job, 'completed', null);

        patch(event.product_id, (current) => ({
          ...current,
          phase: 'installed',
          apply: null,
          error: null,
        }));

        // The record was written a moment before this event, so this is what
        // turns the row's button from Download into Update.
        refreshInstalled();
      }),

      onDownloadFailed((event) => {
        const job = jobs.current.get(event.product_id);
        if (job) reportEnd(job, 'failed', event.retryable ? 'stalled' : 'unknown');

        patch(event.product_id, (current) => ({
          ...current,
          phase: 'failed',
          error: event.message,
        }));
      }),

      /*
       * An expired entitlement token, handled without asking the player.
       *
       * Six hours is shorter than a large download on a slow line, so crossing
       * the boundary is the expected case rather than an error — and it used to
       * be indistinguishable from a dead CDN, because a 403 was counted as a
       * transport failure and the job stalled after five attempts with nothing
       * anywhere saying why. The Rust side now parks instead, and this mints
       * another and hands it over. Nothing is re-fetched.
       */
      onDownloadTokenExpired((event) => {
        void (async () => {
          try {
            await resumeDownload(event.product_id, await mint(event.product_id));
          } catch (cause) {
            patch(event.product_id, (current) => ({
              ...current,
              phase: 'failed',
              error: reason(cause),
            }));
          }
        })();
      }),
    ];

    return () => {
      for (const stop of stops) stop();
    };
  }, [mint, patch, refreshInstalled, reportEnd]);

  const start = useCallback(
    (productId: string, target: InstallTarget, productName: string): void => {
      void (async () => {
        patch(productId, () => ({ ...IDLE, phase: 'preparing' }));

        try {
          /*
           * The manifest and the size index, in one request.
           *
           * They are fetched together because they are useless apart: a
           * manifest says which chunks a build needs and never how large any of
           * them is, so until this endpoint carried both, every download
           * reported zero bytes and no player could see what they were about to
           * spend.
           */
          const { manifest, sizes } = await request<{
            manifest: Manifest;
            sizes: Record<string, number>;
          }>(
            `/launcher/manifest/${encodeURIComponent(target.buildId)}` +
              `?productId=${encodeURIComponent(productId)}`,
            {},
          );
          const { servers } = await request<{ servers: ContentServer[] }>('/launcher/servers', {});
          const entitlementToken = await mint(productId);
          const cap = bandwidthCap();

          const root = libraryRoot();

          const plan = await startDownload({
            product_id: productId,
            // The folder is named after the game rather than its id. The rule
            // that makes a title into a safe path component is Rust's, so this
            // sends the name and not a folder.
            product_name: productName,
            build_id: target.buildId,
            version: target.version,
            manifest,
            /*
             * Real sizes, from the index published beside the manifest.
             *
             * `plan_download` takes this as a *required* argument rather than
             * estimating from an average, because how large a download is is
             * the number a player on a metered connection decides on — and an
             * estimate presented as a fact is worse than asking for the data.
             *
             * Still `{}` for a build published before uploaders sent sizes and
             * too large for the server's fallback sweep. That costs the
             * estimate and nothing else: bytes are counted as they arrive, so
             * progress, speed and the install report are real either way.
             */
            chunk_sizes: sizes,
            servers,
            entitlement_token: entitlementToken,
            concurrency: chunksAtOnce(),
            // Spread rather than passed as undefined, so a launcher with no cap
            // sends no field at all — which is what `Option<u64>` reads as None.
            ...(cap === null ? {} : { bandwidth_cap_bps: cap }),
            ...(root === null ? {} : { library_root: root }),
          });

          jobs.current.set(productId, {
            productId,
            buildId: target.buildId,
            // Honest at last: the Rust side answers this from the install
            // record on disk rather than from whether some earlier download in
            // the same session happened to leave chunks behind.
            kind: plan.is_update ? 'update' : 'install',
            startedAtMs: Date.now(),
            reported: false,
            bytes: 0,
          });

          patch(productId, (current) => ({ ...current, phase: 'running', plan, error: null }));

          /*
           * `started` before a byte moves, and a terminal event later.
           *
           * Two rows per attempt rather than one, because the difference
           * between them is the only way to see an install that was begun and
           * never finished — the player who closed the launcher, the machine
           * that went to sleep, the download nobody came back to. A single
           * terminal event would make those invisible by construction.
           */
          void reportInstall(request, {
            product_id: productId,
            build_id: target.buildId,
            kind: plan.is_update ? 'update' : 'install',
            outcome: 'started',
            platform: currentPlatform(),
            bytes_transferred: 0,
            duration_seconds: null,
            failure_kind: null,
          });
        } catch (cause) {
          patch(productId, (current) => ({ ...current, phase: 'failed', error: reason(cause) }));

          const job = jobs.current.get(productId);
          if (job) reportEnd(job, 'failed', 'unknown');
        }
      })();
    },
    [mint, patch, reportEnd, request],
  );

  const pause = useCallback(
    (productId: string): void => {
      void pauseDownload(productId).catch((cause: unknown) => {
        patch(productId, (current) => ({ ...current, phase: 'failed', error: reason(cause) }));
      });
    },
    [patch],
  );

  const resume = useCallback(
    (productId: string): void => {
      void resumeDownload(productId).catch((cause: unknown) => {
        patch(productId, (current) => ({ ...current, phase: 'failed', error: reason(cause) }));
      });
    },
    [patch],
  );

  const cancel = useCallback(
    (productId: string): void => {
      void cancelDownload(productId)
        .then(() => {
          const job = jobs.current.get(productId);
          if (job) reportEnd(job, 'cancelled', null);
          patch(productId, () => IDLE);
        })
        .catch(() => {
          // Nothing was running, which is the outcome cancelling wanted.
          patch(productId, () => IDLE);
        });
    },
    [patch, reportEnd],
  );

  /**
   * Checks an install by content. The Repair button.
   *
   * **Reachable at last.** `verify` has been on the IPC surface since the shell
   * was written and was called from nowhere, because it took a map of what is
   * on disk and the shell had no disk. It takes a product id now.
   *
   * A verify that finds damage does not fix it here — it says so, and the
   * player presses Download, which re-plans against the chunk store and fetches
   * only what is missing. That is the same path an update takes, which is what
   * keeps it exercised rather than being code that only runs when something has
   * already gone wrong.
   */
  const repair = useCallback((productId: string): void => {
    setChecks((current) => ({ ...current, [productId]: 'Checking…' }));

    void verify(productId)
      .then((report) => {
        setChecks((current) => ({
          ...current,
          [productId]: report.playable
            ? `Verified. ${report.verified.toLocaleString('en-GB')} chunks, all correct.`
            : `${(report.missing + report.corrupt).toLocaleString('en-GB')} of ` +
              `${report.total.toLocaleString('en-GB')} chunks need fetching again. ` +
              'Press Download to repair.',
        }));
      })
      .catch((cause: unknown) => {
        setChecks((current) => ({ ...current, [productId]: reason(cause) }));
      });
  }, []);

  return {
    progressOf,
    installedOf,
    checkOf,
    refreshInstalled,
    start,
    pause,
    resume,
    cancel,
    repair,
  };
}

/* -------------------------------------------------------------------------- */
/* Playing                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Starting games, stopping them, and reporting what was played.
 *
 * ## What a session honestly measures
 *
 * `elapsed_minutes` is real — the minutes the game process was alive, from a
 * monotonic clock. `active_minutes` is **zero**, and that is a statement rather
 * than a placeholder: section 5.4 defines activity as input events and
 * progression telemetry, both of which come from inside the game. A launcher
 * watching from outside sees a process, not a controller.
 *
 * Reporting elapsed time as active would inflate every figure that reads it —
 * refund eligibility, the thirty-minute review gate, First Hundred fulfilment
 * and a studio's public playtime — so the launcher reports what it observed.
 * `sessionTick` stays on the IPC surface taking input and progression counts,
 * which is the seam a game-side SDK reports through.
 *
 * ## Why sessions are reported from here and not from Rust
 *
 * The shell has no API base and no session; the webview has both. So the shell
 * writes each finished session to disk, announces it, and this posts it — and
 * clears it only once the platform has answered. A session played offline
 * survives on disk and reports on the next run under the *same* idempotency
 * key, which is what stops one session becoming four.
 */
function useGames(refreshInstalled: () => void): {
  isRunning: (productId: string) => boolean;
  errorOf: (productId: string) => string | null;
  play: (productId: string, entitlement: LauncherEntitlement) => void;
  stop: (productId: string) => void;
} {
  const request = useApiMutation();
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isRunning = useCallback((productId: string) => running[productId] === true, [running]);
  const errorOf = useCallback((productId: string) => errors[productId] ?? null, [errors]);

  /**
   * Posts one finished session and clears it locally.
   *
   * **Cleared only after the API answered.** A session deleted on a failed
   * report is a session nobody will ever count, and the whole reason it is on
   * disk is that reporting can fail.
   */
  const report = useCallback(
    async (session: PlaySession): Promise<void> => {
      try {
        await request('/launcher/sessions', {
          method: 'POST',
          body: JSON.stringify({
            product_id: session.product_id,
            build_version: session.build_version,
            // The server measures the report against this span and refuses one
            // that claims more minutes than it covers, so both ends are sent.
            started_at: new Date(Date.now() - session.elapsed_minutes * 60_000).toISOString(),
            elapsed_minutes: session.elapsed_minutes,
            active_minutes: session.active_minutes,
            input_events: session.input_events,
            progression_events: session.progression_events,
            crashed: session.crashed,
            idempotency_key: session.idempotency_key,
          }),
        });

        await sessionReported(session.idempotency_key);
      } catch {
        /*
         * Left on disk, deliberately and silently.
         *
         * The ordinary ways to get here are an offline machine and an expired
         * session, neither of which the player can act on — and the report is
         * retried on the next run under the same key. A message here would be
         * an error about bookkeeping on a screen about games.
         */
      }
    },
    [request],
  );

  /*
   * Anything left over from a previous run, plus whatever is running now.
   *
   * The second half is what makes this screen correct after a navigation: the
   * game is the shell's child process, so the shell is the only thing that
   * knows, and a screen that assumed nothing was running would offer Play for a
   * game already open.
   */
  useEffect(() => {
    if (!isLauncher()) return;

    void runningGames()
      .then((titles) => {
        setRunning(Object.fromEntries(titles.map((productId) => [productId, true])));
      })
      .catch(() => {});

    void pendingSessions()
      .then((sessions) => Promise.all(sessions.map(report)))
      .catch(() => {});
  }, [report]);

  useEffect(() => {
    if (!isLauncher()) return;

    const stops = [
      onGameStarted((event) => {
        setRunning((current) => ({ ...current, [event.product_id]: true }));
      }),

      onGameExited((event) => {
        setRunning((current) => ({ ...current, [event.product_id]: false }));
        void report(event.session);

        // A crash may have left files in a state Repair should be offered for,
        // and in any case the record's timestamps have moved.
        refreshInstalled();
      }),
    ];

    return () => {
      for (const stop of stops) stop();
    };
  }, [refreshInstalled, report]);

  const play = useCallback((productId: string, entitlement: LauncherEntitlement): void => {
    void (async () => {
      setErrors((current) => ({ ...current, [productId]: '' }));

      try {
        /*
         * The entitlement decides first, and it is not a formality.
         *
         * Hard rule 19: a game is never revoked as a penalty, so a stale
         * offline cache produces `AllowStale` and the game still starts. What
         * this catches is the cases that are real limits — a playtest wave that
         * closed, an NDA nobody accepted, a version a playtest pins — and
         * folding them into `launch` would put that rule somewhere a screen
         * could skip.
         */
        const decision = await canStart(entitlement, null, Math.floor(Date.now() / 1000));

        if (!decision.allowed) {
          setErrors((current) => ({
            ...current,
            [productId]: describeBlock(decision.blocked_reason),
          }));
          return;
        }

        await launch(productId);
      } catch (cause) {
        setErrors((current) => ({ ...current, [productId]: reason(cause) }));
      }
    })();
  }, []);

  const stop = useCallback((productId: string): void => {
    void stopGame(productId).catch((cause: unknown) => {
      setErrors((current) => ({ ...current, [productId]: reason(cause) }));
    });
  }, []);

  return { isRunning, errorOf, play, stop };
}

/**
 * `BlockReason`, debug-formatted by Rust, as a sentence.
 *
 * The Rust side sends `GrantExpired`, `NdaNotAccepted` or
 * `WrongVersion { required: "…", installed: "…" }` — a `{:?}` of the enum,
 * which is a shape rather than a message. Turning it into English here rather
 * than in Rust keeps the vocabulary in one place, the same split
 * `describeProviderError` makes for sign-in errors.
 */
function describeBlock(blocked: string | null): string {
  if (blocked === null) return 'That game cannot start right now.';
  if (blocked.startsWith('GrantExpired')) {
    return 'This playtest has closed. It was time-limited when it was granted.';
  }
  if (blocked.startsWith('NdaNotAccepted')) {
    return 'Accept the playtest agreement before starting this build.';
  }
  if (blocked.startsWith('WrongVersion')) {
    return 'This playtest requires a specific build. Update before starting it.';
  }
  return 'That game cannot start right now.';
}

/* -------------------------------------------------------------------------- */
/* Update checks                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Asks, on a timer, whether anything installed has a newer build.
 *
 * **The parts were all here and nothing asked.** Every install record carries
 * the manifest digest of the build in its folder, and `/launcher/library`
 * returns the digest each title currently resolves to — so an update check is a
 * comparison, and the only thing missing was something to make it periodically.
 *
 * Checking is free and always on. *Acting* is the setting: `autoUpdate` is off
 * by default because downloading spends a player's bandwidth, which on a
 * metered connection is their money, and a launcher that starts a 40GB transfer
 * during a video call is one people uninstall.
 */
function useUpdateChecks(
  library: LauncherLibrary | undefined,
  refetch: () => void,
  downloads: ReturnType<typeof useDownloads>,
): void {
  useEffect(() => {
    if (!isLauncher()) return;

    const period = updateCheckMinutes() * 60_000;
    const timer = window.setInterval(refetch, period);
    return () => window.clearInterval(timer);
  }, [refetch]);

  /*
   * Acting on what the last refetch said.
   *
   * Separate from the timer so that an update found by the *first* load is
   * acted on too — a launcher opened after a week away should not wait half an
   * hour to notice.
   */
  useEffect(() => {
    if (!isLauncher() || !library || !autoUpdate()) return;

    for (const entitlement of library.entitlements) {
      const productId = entitlement.product_id;
      const target = library.builds[productId];
      if (!target) continue;

      const installed = downloads.installedOf(productId);
      if (!installed || installed.manifest_digest === target.manifestDigest) continue;

      // Only from rest. A title mid-download, mid-install or already running is
      // one this must not interrupt.
      if (downloads.progressOf(productId).phase !== 'idle') continue;

      downloads.start(productId, target, entitlement.product_name);
    }
  }, [library, downloads]);
}

/** Whatever a failure had to say, as a sentence. */
function reason(cause: unknown): string {
  if (cause instanceof Error && cause.message !== '') return cause.message;
  return 'That did not work. Try again.';
}

/* -------------------------------------------------------------------------- */
/* The screen                                                                 */
/* -------------------------------------------------------------------------- */

export function DownloadsRoute(): React.ReactElement {
  /*
   * Registered only in the launcher, and refused here as well.
   *
   * Not belt and braces for its own sake. The registration is the thing that
   * keeps a browser from ever routing here, and it lives in `router.tsx`, which
   * is one file away from this one — so this guard is what makes a mistake
   * there render a sentence instead of a screen whose every button throws
   * `The launcher bridge is not here`. It costs four lines and it is the
   * difference between a wrong route and a broken page.
   */
  if (!isLauncher()) return <NotOutHere />;
  return <Downloads />;
}

function NotOutHere(): React.ReactElement {
  return (
    <Screen title="Downloads" query={{ isPending: false, error: null }}>
      <Empty>
        Downloads happen in the launcher, which is where the games go.{' '}
        <Link href={'/download'}>Get it here</Link>, or see{' '}
        <Link href="/library">what you own</Link>.
      </Empty>
    </Screen>
  );
}

const CELEBRATION_HOLDS_FOR_MS = 4500;

const PHASES_WORTH_WATCHING: readonly Phase[] = [
  'preparing',
  'running',
  'paused',
  'stalled',
  'applying',
];

function pageBackgroundUrlFor(productId: string): string {
  return `${apiOrigin()}${titleArtPath(productId, 'page_background')}`;
}

function headlineFor(phase: Phase): string {
  switch (phase) {
    case 'preparing':
      return 'Working out what to fetch';
    case 'running':
      return 'Downloading';
    case 'paused':
      return 'Paused';
    case 'stalled':
      return 'Stalled';
    case 'applying':
      return 'Writing files';
    case 'installed':
      return 'Ready to play';
    case 'failed':
      return 'That did not work';
    case 'idle':
      return 'Waiting';
  }
}

function shareCompleteFor(progress: Progress): number | null {
  if (progress.phase === 'installed') return 1;

  if (progress.phase === 'applying') {
    const { apply } = progress;
    if (!apply || apply.bytes_total <= 0) return null;
    return apply.bytes_written / apply.bytes_total;
  }

  return progress.live?.progress ?? null;
}

function factsFor(progress: Progress, target: InstallTarget | null): string[] {
  const facts: string[] = [];
  const { live, apply, phase } = progress;

  if (phase === 'applying' && apply && apply.bytes_total > 0) {
    facts.push(`${formatBytes(apply.bytes_written)} of ${formatBytes(apply.bytes_total)} written`);
    return facts;
  }

  if (live) {
    if (live.bytes_downloaded > 0) facts.push(`${formatBytes(live.bytes_downloaded)} down`);
    if (live.bytes_per_second > 0 && phase === 'running') {
      facts.push(`${formatBytes(live.bytes_per_second)} a second`);
    }
    if (live.chunks_remaining > 0) {
      facts.push(`${live.chunks_remaining.toLocaleString('en-GB')} chunks left`);
    }
    if (live.reuse_ratio > 0) {
      facts.push(`${Math.round(live.reuse_ratio * 100)}% already here`);
    }
  }

  if (facts.length === 0 && target) facts.push(`${formatBytes(target.totalBytes)} in total`);

  return facts;
}

function useCelebratedInstall(
  entitlements: readonly LauncherEntitlement[],
  progressOf: (productId: string) => Progress,
): string | null {
  const lastSeenPhases = useRef(new Map<string, Phase>());
  const [justInstalled, setJustInstalled] = useState<string | null>(null);

  const phaseSignature = entitlements
    .map((entitlement) => `${entitlement.product_id}:${progressOf(entitlement.product_id).phase}`)
    .join('|');

  useEffect(() => {
    let arrivedAtInstalled: string | null = null;

    for (const entitlement of entitlements) {
      const productId = entitlement.product_id;
      const phase = progressOf(productId).phase;
      const previousPhase = lastSeenPhases.current.get(productId);
      if (previousPhase !== undefined && previousPhase !== 'installed' && phase === 'installed') {
        arrivedAtInstalled = productId;
      }
      lastSeenPhases.current.set(productId, phase);
    }

    if (!arrivedAtInstalled) return;
    setJustInstalled(arrivedAtInstalled);
    playSound('installFinished');
  }, [phaseSignature, entitlements, progressOf]);

  useEffect(() => {
    if (!justInstalled) return;
    const timer = window.setTimeout(() => setJustInstalled(null), CELEBRATION_HOLDS_FOR_MS);
    return () => window.clearTimeout(timer);
  }, [justInstalled]);

  return justInstalled;
}

function entitlementWorthWatching(
  entitlements: readonly LauncherEntitlement[],
  progressOf: (productId: string) => Progress,
  justInstalledProductId: string | null,
): LauncherEntitlement | null {
  const busy = entitlements.find((entitlement) =>
    PHASES_WORTH_WATCHING.includes(progressOf(entitlement.product_id).phase),
  );
  if (busy) return busy;

  if (!justInstalledProductId) return null;
  return (
    entitlements.find((entitlement) => entitlement.product_id === justInstalledProductId) ?? null
  );
}

function Downloads(): React.ReactElement {
  const request = useApiMutation();
  const query = useApi<LauncherLibrary>(['launcher', 'library'], '/launcher/library');
  const downloads = useDownloads();
  const games = useGames(downloads.refreshInstalled);

  const refetch = useCallback(() => void query.refetch(), [query]);
  useUpdateChecks(query.data, refetch, downloads);

  /*
   * This machine, registered once.
   *
   * Here rather than at the root because registration needs an authenticated
   * request, and this is the first screen that only ever renders for somebody
   * signed in. It is idempotent and swallows its own failures — a launcher that
   * cannot register still downloads, installs and plays.
   */
  useEffect(() => {
    if (!isLauncher()) return;
    void ensureRegistered(request);
  }, [request]);

  const entitlements = query.data?.entitlements ?? [];
  const justInstalledProductId = useCelebratedInstall(entitlements, downloads.progressOf);
  const watched = entitlementWorthWatching(
    entitlements,
    downloads.progressOf,
    justInstalledProductId,
  );
  const watchedProgress = watched ? downloads.progressOf(watched.product_id) : null;
  const watchedTarget = watched ? (query.data?.builds[watched.product_id] ?? null) : null;

  return (
    <Screen title="Downloads" aside={`${entitlements.length} owned`} query={query}>
      <VoltFlash trigger={justInstalledProductId} />

      {watched && watchedProgress ? (
        <InstallTheatre
          gameName={watched.product_name}
          backgroundUrl={pageBackgroundUrlFor(watched.product_id)}
          headline={headlineFor(watchedProgress.phase)}
          shareComplete={shareCompleteFor(watchedProgress)}
          facts={factsFor(watchedProgress, watchedTarget)}
          finished={watchedProgress.phase === 'installed'}
          actions={
            <Actions
              entitlement={watched}
              target={watchedTarget}
              progress={watchedProgress}
              installed={downloads.installedOf(watched.product_id)}
              downloads={downloads}
              games={games}
            />
          }
        />
      ) : null}

      {/*
        Said on the screen rather than only in a comment, because a row with no
        Play button is otherwise a mystery: the reason is the build, not the
        launcher, and only the studio can fix it.
      */}
      <Notice>
        Games download, check themselves, install and play from here. A game with no Play button has
        no file marked as the game yet, and only a new build can fix that.
      </Notice>

      {query.data && entitlements.length === 0 ? (
        <Empty>
          Nothing to download yet. <Link href={'/'}>Look at our games</Link>, or use a gift key.
        </Empty>
      ) : (
        <Table head={['Game', 'Build', 'Size', 'Status', '']}>
          {entitlements.map((entitlement) => (
            <Row
              key={entitlement.product_id}
              entitlement={entitlement}
              target={query.data?.builds[entitlement.product_id] ?? null}
              downloads={downloads}
              games={games}
            />
          ))}
        </Table>
      )}
    </Screen>
  );
}

function Row({
  entitlement,
  target,
  downloads,
  games,
}: {
  entitlement: LauncherEntitlement;
  target: InstallTarget | null;
  downloads: ReturnType<typeof useDownloads>;
  games: ReturnType<typeof useGames>;
}): React.ReactElement {
  const productId = entitlement.product_id;
  const progress = downloads.progressOf(productId);

  return (
    <tr>
      <td>{entitlement.product_name}</td>
      {/*
        Null is an ordinary answer rather than an error: a title can be on sale
        before a build exists, and the honest thing to show is that there is
        nothing to install rather than a failure.
      */}
      <td className={ui.numeric ?? ''}>{target ? target.version : '—'}</td>
      <td className={ui.numeric ?? ''}>{target ? formatBytes(target.totalBytes) : '—'}</td>
      <td>
        <State
          progress={progress}
          target={target}
          installed={downloads.installedOf(productId)}
          // A launch refusal outranks a verify result, which outranks the
          // resting state: each is the answer to something more recent.
          check={games.errorOf(productId) || downloads.checkOf(productId)}
          running={games.isRunning(productId)}
        />
      </td>
      <td>
        <Actions
          entitlement={entitlement}
          target={target}
          progress={progress}
          installed={downloads.installedOf(productId)}
          downloads={downloads}
          games={games}
        />
      </td>
    </tr>
  );
}

function State({
  progress,
  target,
  installed,
  check,
  running,
}: {
  progress: Progress;
  target: InstallTarget | null;
  installed: InstallRecord | null;
  check: string | null;
  running: boolean;
}): React.ReactElement {
  if (!target) return <>No build published yet</>;

  const { phase, live, apply, error } = progress;

  // A game that is open outranks everything else this column could say. It is
  // the most recent thing that happened and the only one still happening.
  if (running) return <span className={ui.statLabel ?? ''}>Running</span>;

  if (phase === 'idle' || phase === 'installed') {
    // A launch refusal or a Repair result outranks the resting state, because
    // it answers something the player just asked for and there is nowhere else
    // to put it.
    if (check) return <span className={ui.statLabel ?? ''}>{check}</span>;

    /*
     * What is on this machine, which this screen could not say before there
     * were install records.
     *
     * The comparison is on the manifest digest rather than the version string:
     * a version is what a studio chose to call the build, so a digest catches a
     * build republished under an unchanged version and a version does not.
     */
    if (installed) {
      const stale = installed.manifest_digest !== target.manifestDigest;
      return (
        <span className={ui.statLabel ?? ''}>
          {stale ? `Installed ${installed.version} · update available` : `Installed · up to date`}
        </span>
      );
    }

    return (
      <span className={ui.statLabel ?? ''}>{target.chunkCount.toLocaleString('en-GB')} chunks</span>
    );
  }

  if (phase === 'preparing') return <>Reading the manifest</>;
  if (phase === 'failed' || phase === 'stalled') {
    return <span className={ui.statLabel ?? ''}>{error ?? 'That did not work.'}</span>;
  }

  /*
   * Writing files, which is its own progress and not the download's.
   *
   * Worth its own line rather than being folded into the bar: an apply of a
   * large build takes minutes, and a bar that sat at 100% throughout would look
   * like the launcher had hung at the very last step.
   */
  if (phase === 'applying') {
    const share = apply && apply.bytes_total > 0 ? apply.bytes_written / apply.bytes_total : 0;
    return (
      <>
        <Bar progress={share} />{' '}
        <span className={ui.statLabel ?? ''}>
          Writing files
          {apply && apply.bytes_total > 0
            ? ` · ${formatBytes(apply.bytes_written)} of ${formatBytes(apply.bytes_total)}`
            : ''}
        </span>
      </>
    );
  }

  if (!live) return <>Starting</>;

  return (
    <>
      <Bar progress={live.progress} />{' '}
      <span className={ui.statLabel ?? ''}>
        {Math.round(live.progress * 100)}% · {live.chunks_remaining.toLocaleString('en-GB')} chunks
        left
        {/*
          A real figure at last. This was the build's total size read off the
          build row, because the driver had no idea what had actually moved;
          the Rust side counts bytes as they arrive, so this is what crossed
          the wire rather than what the manifest hoped.
        */}
        {live.bytes_downloaded > 0 ? ` · ${formatBytes(live.bytes_downloaded)}` : ''}
        {live.bytes_per_second > 0 && phase === 'running'
          ? ` · ${formatBytes(live.bytes_per_second)}/s`
          : ''}
        {live.reuse_ratio > 0
          ? ` · ${Math.round(live.reuse_ratio * 100)}% already on this machine`
          : ''}
        {phase === 'paused' ? ' · paused' : ''}
      </span>
    </>
  );
}

/**
 * Progress, drawn as the Progress Strip.
 *
 * `DownloadStrip` draws one bar per unit, which is right for the four or five
 * it was written for and impossible for a build: two hundred thousand chunks is
 * two hundred thousand elements. So the strip gets a fixed number of bars and a
 * share of them filled, and the sentence beside it carries the real numbers.
 *
 * Hidden from assistive technology on purpose. The component labels itself
 * `13 of 24 complete`, which with quantised bars is a count of bars rather than
 * of chunks — a number about nothing. What a screen reader gets instead is the
 * percentage and the chunk count next to it, which are the facts.
 */
function Bar({ progress }: { progress: number }): React.ReactElement {
  const filled = Math.round(Math.min(1, Math.max(0, progress)) * STRIP_BARS);

  return (
    <span aria-hidden="true">
      <DownloadStrip total={STRIP_BARS} completed={filled} />
    </span>
  );
}

function Actions({
  entitlement,
  target,
  progress,
  installed,
  downloads,
  games,
}: {
  entitlement: LauncherEntitlement;
  target: InstallTarget | null;
  progress: Progress;
  installed: InstallRecord | null;
  downloads: ReturnType<typeof useDownloads>;
  games: ReturnType<typeof useGames>;
}): React.ReactElement | null {
  const productId = entitlement.product_id;
  if (!target) return null;

  const button = (label: string, onClick: () => void, disabled = false, primary = false) => (
    <button
      type="button"
      className={(primary ? ui.button : ui.buttonSecondary) ?? ''}
      disabled={disabled}
      onClick={onClick}
      {...soundedPointerHandlers}
    >
      {label}
    </button>
  );

  const { phase } = progress;

  // A game this launcher started is a game this launcher can close, whatever
  // else the row might otherwise be offering.
  if (games.isRunning(productId)) {
    return button('Stop', () => games.stop(productId));
  }

  if (phase === 'idle' || phase === 'installed') {
    /*
     * "Update" is a claim this screen can finally make.
     *
     * It used to say Download and never Update, because nothing here knew what
     * was installed — and a button offering to update a game that is not on the
     * machine is a worse wrong answer than one offering to download a game that
     * already is. There is a record on disk now, with the digest of the build
     * in the folder, so the claim is checkable.
     *
     * An install left mid-apply says Finish: pressing it starts the same
     * download, which finds every chunk already local and goes straight to
     * writing the files that were never written.
     */
    const half = installed?.state.state === 'applying';
    const stale = installed !== null && installed.manifest_digest !== target.manifestDigest;

    /*
     * Play first, when there is one.
     *
     * Ahead of Update deliberately: a player who opened the launcher to play a
     * game should not have to wait for a patch they did not ask for, and an
     * update that has been waiting an hour can wait another one. Steam gets
     * this backwards and it is one of the most-complained-about things about
     * it.
     *
     * Absent when the installed build never said how to start itself — every
     * build published before manifests carried a launch configuration. A button
     * that answers with an apology is worse than no button.
     */
    const playable = installed !== null && !half && installed.launchable;

    const label = half ? 'Finish' : stale ? 'Update' : installed ? 'Verify' : 'Download';
    const action = () =>
      label === 'Verify'
        ? downloads.repair(productId)
        : downloads.start(productId, target, entitlement.product_name);

    return (
      <>
        {playable ? button('Play', () => games.play(productId, entitlement), false, true) : null}
        {button(label, action)}
        {/* Repair is the same download, so it is only worth its own button
            where Download is not already the thing on offer. */}
        {installed && !half && stale ? button('Verify', () => downloads.repair(productId)) : null}
      </>
    );
  }

  if (phase === 'preparing') return button('Preparing', () => {}, true);
  if (phase === 'applying') return button('Installing', () => {}, true);

  if (phase === 'running') {
    return (
      <>
        {button('Pause', () => downloads.pause(productId))}
        {button('Cancel', () => downloads.cancel(productId))}
      </>
    );
  }

  if (phase === 'paused') {
    return (
      <>
        {button('Resume', () => downloads.resume(productId))}
        {button('Cancel', () => downloads.cancel(productId))}
      </>
    );
  }

  /*
   * Stalled or failed, and the button starts the download again rather than
   * resuming it.
   *
   * A stalled job's workers have already exited, so there is nothing to resume
   * — and re-planning is the better answer anyway: it re-reads the chunk store,
   * counts everything that did land and fetches only the remainder. A retry
   * costs the bytes that are missing and not the ones that arrived.
   */
  return button('Try again', () => downloads.start(productId, target, entitlement.product_name));
}
